// src/ui/screens/char-select/CharacterCard.tsx — P5 D-26 grid card.
//
// Mounted: once per filtered (unlocked===true) character in the 3-column
// CharacterSelect grid. Tap → opens the detail pane via onSelect(id).
//
// Content stack (top to bottom, per 05-UI-SPEC §Layout Contracts — Grid state):
//   1. Portrait image (80% of card width, 1:1 ratio)
//   2. inGameName (16px semibold, cream, centered, ellipsis)
//   3. Quirk one-liner (12px muted slate, centered, 1 line)
//      — first sentence OR first 40 chars with "...", whichever is shorter.
//
// The card is a native <button> so keyboard focus + Enter/Space activation
// work for free; touch activation via onClick matches the chip pattern from
// RoundSetup.tsx.

import type { JSX } from 'react';

import type { Character } from '../../../shared/content/types';
import { resolvePortrait } from '../../assets/portraits';

export interface CharacterCardProps {
  character: Character;
  onSelect: (id: string) => void;
}

// First-sentence OR 40-char truncation per 05-UI-SPEC §Layout Contracts.
// Split on ". ", "! ", "? " so a sentence that contains a period inside
// (e.g. "Mr. Bean") isn't truncated mid-abbreviation.
function quirkOneLiner(quirk: string): string {
  const firstSentence = quirk.split(/[.!?]\s/)[0] ?? quirk;
  if (firstSentence.length > 40) {
    return firstSentence.slice(0, 40).trimEnd() + '...';
  }
  return firstSentence.length > 0 ? firstSentence : '…';
}

export function CharacterCard({ character, onSelect }: CharacterCardProps): JSX.Element {
  return (
    <button
      type="button"
      className="character-card"
      data-testid={`character-card-${character.id}`}
      onClick={() => onSelect(character.id)}
    >
      <img
        className="character-card__portrait"
        src={resolvePortrait(character.portraitId)}
        alt={`Portrait of ${character.inGameName}`}
      />
      <div className="character-card__name">{character.inGameName}</div>
      <div className="character-card__quirk">{quirkOneLiner(character.quirk)}</div>
    </button>
  );
}
